/** Accept only product paths on this storefront, with a selected variant / theme preview. */
export function productReturnPath(value, origin) {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return null;
  try {
    const url = new URL(value, origin);
    if (url.origin !== origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[a-z0-9][a-z0-9-]*\/?$/i.test(url.pathname)) return null;
    const query = new URLSearchParams();
    for (const key of ['variant', 'preview_theme_id']) {
      const id = url.searchParams.get(key);
      if (id && /^\d+$/.test(id)) query.set(key, id);
    }
    return url.pathname + (query.size ? `?${query}` : '');
  } catch {
    return null;
  }
}

export function previewAnchor(family, format) {
  if (family === 'magnet') return 'fridge-magnets';
  if (family === 'greeting-card') return 'greeting-cards';
  return String(format).toLowerCase() === 'unframed' ? 'unframed-prints' : 'framed-prints';
}

if (typeof customElements !== 'undefined') {
  class ProductVideoLink extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.updateSizeHelp(this.dataset.selectedFormat);
      document.addEventListener('variant:update', (event) => {
        if (String(event.detail?.data?.productId) !== this.dataset.productId) return;
        const variant = event.detail.resource;
        if (!variant) return;
        this.updateSizeHelp(variant.options?.[Number(this.dataset.formatIndex)]);
        const link = this.querySelector('[data-video-preview-link]');
        if (!link) return;
        const url = new URL(link.href);
        const path = productReturnPath(url.searchParams.get('return_to'), location.origin);
        if (!path) return;
        const product = new URL(path, location.origin);
        product.searchParams.set('variant', variant.id);
        url.searchParams.set('return_to', product.pathname + product.search);
        url.hash = previewAnchor(this.dataset.family, variant.options?.[Number(this.dataset.formatIndex)]);
        link.href = url.href;
      }, { signal: this.abort.signal });
      // Carry the preview theme through the round trip when reviewing an unpublished theme.
      this.addEventListener('click', () => {
        const link = this.querySelector('[data-video-preview-link]');
        if (!link) return;
        const url = new URL(link.href);
        const previewId = new URL(location.href).searchParams.get('preview_theme_id');
        if (previewId && /^\d+$/.test(previewId)) {
          url.searchParams.set('preview_theme_id', previewId);
          const path = productReturnPath(url.searchParams.get('return_to'), location.origin);
          if (path) {
            const product = new URL(path, location.origin);
            product.searchParams.set('preview_theme_id', previewId);
            url.searchParams.set('return_to', product.pathname + product.search);
          }
          link.href = url.href;
        }
      }, { signal: this.abort.signal });
    }
    updateSizeHelp(format) {
      const title = this.querySelector('[data-size-help-title]');
      if (!title) return;
      const unframed = String(format).toLowerCase() === 'unframed';
      title.textContent = unframed ? 'Help choosing a size or border' : 'Help choosing a size';
      const borderHelp = this.querySelector('[data-border-help]');
      if (borderHelp) borderHelp.hidden = !unframed;
    }
    disconnectedCallback() { this.abort?.abort(); }
  }

  class ProductVideos extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.players = new Map();
      const path = productReturnPath(new URL(location.href).searchParams.get('return_to'), location.origin);
      this.querySelectorAll('[data-video-return]').forEach((link) => {
        link.hidden = !path;
        if (path) link.href = path;
      });
      this.querySelectorAll('.cp-videos__figure').forEach((figure) => {
        const video = figure.querySelector('video');
        const play = figure.querySelector('button');
        const error = figure.querySelector('[role="status"]');
        const state = { video, play, error, visible: false, manualPaused: false, automaticPause: false, pending: false };
        this.players.set(video, state);
        video.muted = true;
        video.loop = true;
        play.hidden = false;
        const toggle = () => {
          if (video.paused) {
            state.manualPaused = false;
            this.start(state, true);
          } else {
            video.pause();
          }
        };
        play.addEventListener('click', () => {
          state.manualPaused = false;
          this.start(state, true);
        }, options);
        video.addEventListener('click', toggle, options);
        video.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          toggle();
        }, options);
        video.addEventListener('play', () => {
          state.manualPaused = false;
          play.hidden = true;
          error.hidden = true;
          video.ariaLabel = `Pause ${video.dataset?.videoLabel || 'video'} preview`;
          if (!state.visible || document.hidden) this.pause(state);
        }, options);
        video.addEventListener('pause', () => {
          if (state.automaticPause) state.automaticPause = false;
          else state.manualPaused = true;
          play.hidden = false;
          video.ariaLabel = `Play ${video.dataset?.videoLabel || 'video'} preview`;
        }, options);
        const failed = () => { play.hidden = false; error.hidden = false; };
        video.addEventListener('error', failed, options);
        video.querySelector('source')?.addEventListener('error', failed, options);
      });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const state = this.players.get(entry.target);
            if (!state) return;
            state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.25;
            this.sync(state);
          });
        }, { threshold: [0, 0.25] });
        this.players.forEach(({ video }) => this.observer.observe(video));
      } else {
        // Older browsers retain the custom click-to-play fallback.
        this.players.forEach((state) => { state.visible = true; });
      }
      document.addEventListener('visibilitychange', () => this.players.forEach((state) => this.sync(state)), options);
      this.motion.addEventListener('change', () => this.players.forEach((state) => this.sync(state)), options);
    }
    sync(state) {
      if (!state.visible || document.hidden || this.motion.matches) this.pause(state);
      else if (!state.manualPaused && this.observer) this.start(state);
    }
    pause(state) {
      if (!state.video.paused) {
        state.automaticPause = true;
        state.video.pause();
      }
    }
    async start(state, manual = false) {
      if (state.pending || !state.video.paused) return;
      state.pending = true;
      state.play.hidden = true;
      state.error.hidden = true;
      try {
        await state.video.play();
        // The visitor may scroll away or switch tabs while play() is pending.
        if (!this.isConnected || !state.visible || document.hidden || (!manual && this.motion.matches)) this.pause(state);
        else if (manual) state.video.focus();
      } catch {
        state.play.hidden = false;
        // Autoplay restrictions are expected on some devices: keep a usable play button.
        if (manual) state.error.hidden = false;
      } finally {
        state.pending = false;
      }
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.abort?.abort();
      this.players?.forEach((state) => this.pause(state));
    }
  }
  if (!customElements.get('cp-product-video-link')) customElements.define('cp-product-video-link', ProductVideoLink);
  if (!customElements.get('cp-product-videos')) customElements.define('cp-product-videos', ProductVideos);
}
